import { LabelDisplayedRowsArgs, Paper, TableContainer, TablePagination } from '@mui/material';
import React from 'react';

import { EmptyContent } from '@/shared/components';
import { globalConstants, globalTypes } from '@/shared/duck';
import { muiPalette } from '@/shared/general-theme';
import { useMedia } from '@/shared/hooks';

import { TableContent } from './components';
import { tableTypes } from './duck';

interface TableProps<DataItem> extends tableTypes.MuiTableProps<DataItem> {
  data: DataItem[],
  columns: tableTypes.ColumnProps<DataItem>[];
}

const UITable = <DataItem extends globalTypes.UIDItem>({
  data,
  columns,
  loading,
  pagination,
  emptyContentTitle,
  emptyContentSubTitle,
  ...props
}: TableProps<DataItem>) => {
  const { isSmallScreen } = useMedia();

  const maxHeight = React.useMemo(() => {
    const footerHeight = pagination ? '3.25rem' : '0.1rem';
    if (loading) {
      return data.length ? `calc(100% - ${footerHeight})` : 'initial';
    } else {
      return `calc(100% - ${footerHeight})`;
    }
  }, [loading, data, pagination]);

  React.useEffect(() => {
    if (!data.length && !loading && pagination?.page && pagination.page > 0) {
      return pagination?.onPageChange && pagination?.onPageChange(null, pagination.page - 1);
    }
  }, [data, loading, pagination]);

  const customLabelDisplayedRows = React.useCallback(
    (paginationInfo: LabelDisplayedRowsArgs) => {
      const currentPage = paginationInfo.page + 1;
      return `${isSmallScreen ? '' : 'Page'} ${currentPage} of ${paginationInfo.count}`;
    }, [isSmallScreen],
  );

  return (
    <Paper
      sx={{
        width: '100%',
        boxShadow: 'initial',
        boxSizing: 'border-box',
        border: `1px solid ${muiPalette.divider}`,
        backgroundColor: muiPalette.background?.paper,
        overflow: 'hidden',
      }}
    >
      <TableContainer sx={{ maxHeight: maxHeight }}>
        <TableContent<DataItem>
          data={data}
          columns={columns}
          loading={loading}
          {...props}
        />
      </TableContainer>

      {!loading && !data.length && (
        <EmptyContent
          title={emptyContentTitle || 'No items found'}
          subtitle={emptyContentSubTitle}
        />
      )}

      {data.length && pagination ? (
        <TablePagination
          component='div'
          rowsPerPageOptions={globalConstants.ITEMS_PER_PAGE_OPTIONS}
          count={pagination.count}
          rowsPerPage={pagination.rowsPerPage}
          page={pagination.page}
          onPageChange={pagination.onPageChange}
          onRowsPerPageChange={pagination.onRowsPerPageChange}
          labelDisplayedRows={(paginationInfo) => customLabelDisplayedRows({
            ...paginationInfo,
            count: pagination.totalPages,
          })}
          showFirstButton={!isSmallScreen}
          showLastButton={!isSmallScreen}
        />
      ) : null}
    </Paper>
  );

};

export default UITable;